import * as React from "react";
import { useState, useRef } from "react";
import { Badge } from "../badge";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../dropdown-menu";
import { Input, Tooltip, Card, Dialog } from "@tellescope/design-system";
import {
  MoreVertical,
  Edit3,
  Trash2,
  Download,
  FileText,
  Tag,
  MessageSquare,
  Copy,
  Pin,
  Plus,
  Circle,
} from "lucide-react";

export interface NoteTag {
  id: string;
  name: string;
  color: string;
}

export interface NoteTimelineCardProps {
  title: string;
  content: string;
  date: string;
  author: string;
  lastActivity?: string;
  appliedTags?: NoteTag[];
  availableTags?: NoteTag[];
  pinned?: boolean;
  onEditNote?: () => void;
  onDeleteNote?: () => void;
  onDownloadPdf?: () => void;
  onGeneratePdf?: () => void;
  onLogDiscussion?: () => void;
  onCopyId?: () => void;
  onPin?: () => void;
  onTagAdd?: (tagId: string) => void;
  onTagRemove?: (tagId: string) => void;
  onTagCreate?: (name: string, color: string) => void;
}

// Available colors for tag creation
const tagColors = [
  { name: "Red", value: "#ef4444" },
  { name: "Orange", value: "#f97316" },
  { name: "Yellow", value: "#eab308" },
  { name: "Green", value: "#22c55e" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Purple", value: "#8b5cf6" },
  { name: "Pink", value: "#ec4899" },
  { name: "Gray", value: "#6b7280" },
];

const NoteMenu = React.forwardRef<
  React.ElementRef<typeof DropdownMenuTrigger>,
  {
    appliedTags?: NoteTag[];
    availableTags?: NoteTag[];
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    tagSubmenuOpen?: boolean;
    onTagSubmenuOpenChange?: (open: boolean) => void;
    onEditNote?: () => void;
    onDeleteNote?: () => void;
    onDownloadPdf?: () => void;
    onGeneratePdf?: () => void;
    onLogDiscussion?: () => void;
    onCopyId?: () => void;
    onPin?: () => void;
    onTagAdd?: (tagId: string) => void;
    onTagRemove?: (tagId: string) => void;
    onTagCreate?: (name: string, color: string) => void;
  }
>(
  (
    {
      appliedTags = [],
      availableTags = [],
      open,
      onOpenChange,
      tagSubmenuOpen,
      onTagSubmenuOpenChange,
      onEditNote,
      onDeleteNote,
      onDownloadPdf,
      onGeneratePdf,
      onLogDiscussion,
      onCopyId,
      onPin,
      onTagAdd,
      onTagRemove,
      onTagCreate,
    },
    ref
  ) => {
    const [newTagName, setNewTagName] = useState("");
    const [selectedColor, setSelectedColor] = useState(tagColors[0]?.value ?? "#ef4444");
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const handleTagToggle = (tagId: string) => {
      const isSelected = appliedTags.some((tag) => tag.id === tagId);
      if (isSelected) {
        onTagRemove?.(tagId);
      } else {
        onTagAdd?.(tagId);
      }
    };

    const handleCreateTag = () => {
      if (newTagName.trim()) {
        onTagCreate?.(newTagName, selectedColor);
        setNewTagName("");
        setSelectedColor(tagColors[0]?.value ?? "#ef4444");
        setIsCreateDialogOpen(false);
      }
    };

    return (
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <Tooltip>
          <Tooltip.Trigger asChild>
            <DropdownMenuTrigger asChild ref={ref}>
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4 text-foreground" />
              </Button>
            </DropdownMenuTrigger>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <p>More actions</p>
          </Tooltip.Content>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={onEditNote}>
            <Edit3 className="mr-2 h-4 w-4" />
            Edit Note
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDeleteNote}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Note
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDownloadPdf}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onGeneratePdf}>
            <FileText className="mr-2 h-4 w-4" />
            Generate PDF
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuSub open={tagSubmenuOpen} onOpenChange={onTagSubmenuOpenChange}>
            <DropdownMenuSubTrigger className="!gap-2">
              <Tag className="h-4 w-4 !mr-2 text-muted-foreground" />
              Add Tag
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64">
              <div className="p-2">
                <div className="text-xs font-medium text-muted-foreground mb-2">Existing Tags</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {availableTags.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex items-center justify-between p-1.5 rounded hover:bg-muted cursor-pointer"
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Circle
                          className="h-3 w-3"
                          style={{
                            color: tag.color,
                            fill: appliedTags.some((appliedTag) => appliedTag.id === tag.id)
                              ? tag.color
                              : "transparent",
                          }}
                        />
                        <span className="text-sm">{tag.name}</span>
                      </div>
                      {appliedTags.some((appliedTag) => appliedTag.id === tag.id) && (
                        <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                          Applied
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t pt-2 mt-2">
                  <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <Dialog.Trigger asChild>
                      <Button variant="ghost" size="sm" className="w-full justify-start">
                        <Plus className="mr-2 h-3 w-3" />
                        Create New Tag
                      </Button>
                    </Dialog.Trigger>
                    <Dialog.Content className="sm:max-w-md">
                      <Dialog.Header>
                        <Dialog.Title>Create New Tag</Dialog.Title>
                      </Dialog.Header>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Tag Name</label>
                          <Input
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            placeholder="Enter tag name"
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium">Color</label>
                          <div className="grid grid-cols-4 gap-2">
                            {tagColors.map((color) => (
                              <button
                                key={color.value}
                                type="button"
                                className={`w-8 h-8 rounded-full border-2 ${
                                  selectedColor === color.value ? "border-foreground" : "border-muted"
                                }`}
                                style={{ backgroundColor: color.value }}
                                onClick={() => setSelectedColor(color.value)}
                                title={color.name}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="w-4 h-4 rounded border" style={{ backgroundColor: selectedColor }} />
                            <span className="text-sm text-muted-foreground">
                              Selected: {tagColors.find((c) => c.value === selectedColor)?.name}
                            </span>
                          </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleCreateTag} disabled={!newTagName.trim()}>
                            Create Tag
                          </Button>
                        </div>
                      </div>
                    </Dialog.Content>
                  </Dialog>
                </div>
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={onLogDiscussion}>
            <MessageSquare className="mr-2 h-4 w-4" />
            Log Discussion
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onCopyId}>
            <Copy className="mr-2 h-4 w-4" />
            Copy ID
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onPin}>
            <Pin className="mr-2 h-4 w-4" />
            Pin
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

NoteMenu.displayName = "NoteMenu";

export function NoteTimelineCard({
  title,
  content,
  date,
  author,
  lastActivity,
  appliedTags = [],
  availableTags = [],
  pinned = false,
  onEditNote,
  onDeleteNote,
  onDownloadPdf,
  onGeneratePdf,
  onLogDiscussion,
  onCopyId,
  onPin,
  onTagAdd,
  onTagRemove,
  onTagCreate,
}: NoteTimelineCardProps) {
  const menuRef = useRef<React.ElementRef<typeof DropdownMenuTrigger>>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTagSubmenuOpen, setIsTagSubmenuOpen] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);

  // Tag management state for separate dropdown (shared with main menu)
  const [newTagName, setNewTagName] = useState("");
  const [selectedColor, setSelectedColor] = useState(tagColors[0]?.value ?? "#ef4444");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const handleTagToggle = (tagId: string) => {
    // Use the same handlers as the main menu
    const isSelected = appliedTags.some((tag) => tag.id === tagId);
    if (isSelected) {
      onTagRemove?.(tagId);
    } else {
      onTagAdd?.(tagId);
    }
  };

  const handleCreateTag = () => {
    if (newTagName.trim()) {
      onTagCreate?.(newTagName, selectedColor);
      setNewTagName("");
      setSelectedColor(tagColors[0]?.value ?? "#ef4444");
      setIsCreateDialogOpen(false);
    }
  };

  return (
    <Card
      variant="floating"
      padding="root"
      className="rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
    >
      <Card.Header className="px-6 pt-0 pb-0 space-y-4">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 pr-4">
              <h3 className="text-lg font-semibold text-card-foreground">{title}</h3>
            </div>
            <NoteMenu
              ref={menuRef}
              appliedTags={appliedTags}
              availableTags={availableTags}
              open={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              tagSubmenuOpen={isTagSubmenuOpen}
              onTagSubmenuOpenChange={setIsTagSubmenuOpen}
              onEditNote={onEditNote}
              onDeleteNote={onDeleteNote}
              onDownloadPdf={onDownloadPdf}
              onGeneratePdf={onGeneratePdf}
              onLogDiscussion={onLogDiscussion}
              onCopyId={onCopyId}
              onPin={onPin}
              onTagAdd={onTagAdd}
              onTagRemove={onTagRemove}
              onTagCreate={onTagCreate}
            />
          </div>

          <div className="flex items-center">
            <span className="text-sm text-foreground pr-2">Note</span>
            <span className="text-sm text-muted-foreground">by {author}</span>
          </div>

          <p className="text-sm text-muted-foreground line-clamp-2">{content}</p>

          {appliedTags.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-hidden">
              {appliedTags.slice(0, 2).map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="text-xs whitespace-nowrap flex-shrink-0"
                  style={{ borderColor: tag.color, color: tag.color }}
                >
                  {tag.name}
                </Badge>
              ))}
              {appliedTags.length > 2 && (
                <DropdownMenu open={isTagDropdownOpen} onOpenChange={setIsTagDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs text-muted-foreground border-muted-foreground flex-shrink-0 cursor-pointer hover:bg-muted h-6 px-2.5 py-0.5"
                    >
                      +{appliedTags.length - 2} more
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-64">
                    <div className="p-2">
                      <div className="text-xs font-medium text-muted-foreground mb-2">Existing Tags</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {availableTags.map((tag) => (
                          <div
                            key={tag.id}
                            className="flex items-center justify-between p-1.5 rounded hover:bg-muted cursor-pointer"
                            onClick={() => handleTagToggle(tag.id)}
                          >
                            <div className="flex items-center gap-2">
                              <Circle
                                className="h-3 w-3"
                                style={{
                                  color: tag.color,
                                  fill: appliedTags.some((appliedTag) => appliedTag.id === tag.id)
                                    ? tag.color
                                    : "transparent",
                                }}
                              />
                              <span className="text-sm">{tag.name}</span>
                            </div>
                            {appliedTags.some((appliedTag) => appliedTag.id === tag.id) && (
                              <Badge variant="secondary" className="h-4 px-1.5 text-xs">
                                Applied
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-2 mt-2">
                        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                          <Dialog.Trigger asChild>
                            <Button variant="ghost" size="sm" className="w-full justify-start">
                              <Plus className="mr-2 h-3 w-3" />
                              Create New Tag
                            </Button>
                          </Dialog.Trigger>
                          <Dialog.Content className="sm:max-w-md">
                            <Dialog.Header>
                              <Dialog.Title>Create New Tag</Dialog.Title>
                            </Dialog.Header>
                            <div className="space-y-4 pt-4">
                              <div className="space-y-2">
                                <label className="text-sm font-medium">Tag Name</label>
                                <Input
                                  value={newTagName}
                                  onChange={(e) => setNewTagName(e.target.value)}
                                  placeholder="Enter tag name"
                                  className="w-full"
                                />
                              </div>

                              <div className="space-y-2">
                                <label className="text-sm font-medium">Color</label>
                                <div className="grid grid-cols-4 gap-2">
                                  {tagColors.map((color) => (
                                    <button
                                      key={color.value}
                                      type="button"
                                      className={`w-8 h-8 rounded-full border-2 ${
                                        selectedColor === color.value ? "border-foreground" : "border-muted"
                                      }`}
                                      style={{ backgroundColor: color.value }}
                                      onClick={() => setSelectedColor(color.value)}
                                      title={color.name}
                                    />
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <div className="w-4 h-4 rounded border" style={{ backgroundColor: selectedColor }} />
                                  <span className="text-sm text-muted-foreground">
                                    Selected: {tagColors.find((c) => c.value === selectedColor)?.name}
                                  </span>
                                </div>
                              </div>

                              <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                                  Cancel
                                </Button>
                                <Button onClick={handleCreateTag} disabled={!newTagName.trim()}>
                                  Create Tag
                                </Button>
                              </div>
                            </div>
                          </Dialog.Content>
                        </Dialog>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </Card.Header>

      <Card.Footer className="px-6 pt-6 pb-0 flex items-center justify-between border-t">
        <div className="flex items-center space-x-3">
          {lastActivity && (
            <>
              <div className="flex space-x-3">
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-foreground">{lastActivity}</span>
                </div>
              </div>

              <div className="h-5 w-px bg-border"></div>
            </>
          )}

          <span className="text-sm text-muted-foreground">{date}</span>
        </div>

        <Tooltip>
          <Tooltip.Trigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onPin?.();
              }}
            >
              <Pin
                className={`h-4 w-4 transition-colors ${
                  pinned ? "text-primary fill-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              />
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <p>{pinned ? "Unpin" : "Pin"}</p>
          </Tooltip.Content>
        </Tooltip>
      </Card.Footer>
    </Card>
  );
}
